/**
 * Admin Bundles Page
 *
 * Displays list of all NFT bundles with status filters, seller search,
 * sortable columns, and pagination. Shows stats cards for quick overview.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';

// ==================== API ====================

const API_BASE = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

async function fetchWithAuth(endpoint: string) {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ==================== Types ====================

interface BundleListItem {
  id: string;
  seller_address: string;
  collection_id: string;
  price_drops: string;
  status: string;
  buyer_address: string | null;
  payment_tx_hash: string | null;
  expires_at: string | null;
  sold_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  item_count: number;
  seller_username: string | null;
  buyer_username: string | null;
  collection_name: string | null;
  collection_slug: string | null;
  collection_image_url: string | null;
}

interface BundleStats {
  statusCounts: {
    live: number;
    sold: number;
    cancelled: number;
    expired: number;
    pending: number;
  };
  revenue: {
    totalSoldVolumeXrp: number;
    platformFeesXrp: number;
  };
  last24h: {
    bundlesSold: number;
    volumeXrp: number;
  };
  averages: {
    avgBundlePriceXrp: number;
    avgItemsPerBundle: number;
  };
}

interface BundleListResponse {
  bundles: BundleListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statusCounts: Record<string, number>;
}

type SortField = 'created_at' | 'price_drops' | 'expires_at';

interface StatusTab {
  key: string;
  label: string;
  statuses: string;
}

const STATUS_TABS: StatusTab[] = [
  { key: 'all', label: 'All', statuses: '' },
  { key: 'live', label: 'Live', statuses: 'live' },
  { key: 'sold', label: 'Sold', statuses: 'sold' },
  { key: 'inactive', label: 'Cancelled/Expired', statuses: 'cancelled,expired' },
  { key: 'pending', label: 'Pending', statuses: 'pending' },
];

// ==================== Helpers ====================

function formatXrp(drops: string | number | null | undefined): string {
  if (drops === null || drops === undefined || drops === '0') return '-';
  return `${(Number(drops) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} XRP`;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function truncateId(id: string): string {
  return `${id.slice(0, 8)}...`;
}

function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

function getBundleStatusColor(status: string): string {
  switch (status) {
    case 'live':
      return 'bg-green-100 text-green-700';
    case 'sold':
      return 'bg-blue-100 text-blue-700';
    case 'cancelled':
      return 'bg-red-100 text-red-700';
    case 'expired':
      return 'bg-gray-100 text-gray-600';
    case 'pending':
      return 'bg-yellow-100 text-yellow-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

// ==================== Sort Header Component ====================

function SortHeader({
  label,
  field,
  currentSort,
  currentOrder,
  onSort,
}: {
  label: string;
  field: SortField;
  currentSort: SortField;
  currentOrder: 'asc' | 'desc';
  onSort: (field: SortField) => void;
}) {
  const isActive = currentSort === field;
  return (
    <th
      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:text-gray-700 select-none"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive && (
          <span className="text-blue-600">
            {currentOrder === 'asc' ? '\u2191' : '\u2193'}
          </span>
        )}
      </div>
    </th>
  );
}

// ==================== Main Component ====================

export function AdminBundlesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<BundleListResponse | null>(null);
  const [stats, setStats] = useState<BundleStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sellerInput, setSellerInput] = useState('');
  const [sellerFilter, setSellerFilter] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const tab = STATUS_TABS.find((t) => t.key === activeTab);
      const statusParam = tab?.statuses || undefined;

      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '50');
      if (statusParam) params.set('status', statusParam);
      if (sellerFilter) params.set('seller', sellerFilter);
      params.set('sort', sortField);
      params.set('order', sortOrder);

      const query = params.toString();
      const result = await fetchWithAuth(`/admin/bundles${query ? `?${query}` : ''}`);
      if (result.success) {
        setData(result.data);
      } else {
        throw new Error(result.error || 'Failed to load bundles');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load bundles');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, sortField, sortOrder, sellerFilter]);

  const loadStats = useCallback(async () => {
    try {
      const result = await fetchWithAuth('/admin/bundles/stats');
      if (result.success) {
        setStats(result.data);
      }
    } catch {
      // Stats failure is non-critical
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
    setPage(1);
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleSellerSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSellerFilter(sellerInput.trim());
    setPage(1);
  };

  const handleClearSeller = () => {
    setSellerInput('');
    setSellerFilter('');
    setPage(1);
  };

  const handleCopyId = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const bundles = data?.bundles || [];
  const statusCounts = data?.statusCounts || {};
  const pagination = data?.pagination;

  // Calculate tab counts
  const getTabCount = (tab: StatusTab): number => {
    if (tab.key === 'all') {
      return Object.values(statusCounts).reduce((a, b) => a + b, 0);
    }
    const statuses = tab.statuses.split(',');
    return statuses.reduce((sum, s) => sum + (statusCounts[s] || 0), 0);
  };

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Bundles</h1>
            <p className="mt-2 text-sm text-gray-600">
              Monitor NFT bundle sales
            </p>
          </div>
          <button
            onClick={() => { loadData(); loadStats(); }}
            disabled={loading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="mb-6 grid gap-4 sm:grid-cols-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-green-600">Live Bundles</div>
              <div className="mt-1 text-2xl font-bold text-green-700">{stats.statusCounts.live}</div>
            </div>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-blue-600">Sold (All-time)</div>
              <div className="mt-1 text-2xl font-bold text-blue-700">{stats.statusCounts.sold}</div>
            </div>
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-purple-600">24h Volume</div>
              <div className="mt-1 text-2xl font-bold text-purple-700">
                {stats.last24h.volumeXrp.toLocaleString(undefined, { maximumFractionDigits: 0 })} XRP
              </div>
              <div className="mt-0.5 text-xs text-purple-500">{stats.last24h.bundlesSold} sold</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-medium text-gray-500">Platform Revenue</div>
              <div className="mt-1 text-2xl font-bold text-gray-900">
                {stats.revenue.platformFeesXrp.toLocaleString(undefined, { maximumFractionDigits: 0 })} XRP
              </div>
              <div className="mt-0.5 text-xs text-gray-400">
                Vol: {stats.revenue.totalSoldVolumeXrp.toLocaleString(undefined, { maximumFractionDigits: 0 })} XRP
              </div>
            </div>
          </div>
        )}

        {/* Filter Row: Tabs + Seller Search */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 pb-4">
          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => {
              const count = getTabCount(tab);
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${
                      isActive ? 'bg-blue-200' : 'bg-gray-200'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <form onSubmit={handleSellerSearch} className="flex items-center gap-2">
            <input
              type="text"
              value={sellerInput}
              onChange={(e) => setSellerInput(e.target.value)}
              placeholder="Filter by seller address..."
              className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="submit"
              className="rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Search
            </button>
            {sellerFilter && (
              <button
                type="button"
                onClick={handleClearSeller}
                className="rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-200"
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Error State */}
        {error && (
          <ErrorDisplay error={error} onRetry={loadData} className="mb-6" />
        )}

        {/* Loading State */}
        {loading && bundles.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-sm text-gray-600">Loading bundles...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && bundles.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
            <p className="text-gray-500">
              {sellerFilter
                ? `No bundles found for seller ${truncateAddress(sellerFilter)}.`
                : activeTab === 'all'
                  ? 'No bundles found.'
                  : `No ${activeTab} bundles found.`}
            </p>
          </div>
        )}

        {/* Bundles Table */}
        {bundles.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Bundle ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Seller
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Collection
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Items
                    </th>
                    <SortHeader label="Price" field="price_drops" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <SortHeader label="Created" field="created_at" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                    <SortHeader label="Expires/Sold" field="expires_at" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {bundles.map((bundle) => (
                    <tr
                      key={bundle.id}
                      onClick={() => navigate(`/admin/bundles/${bundle.id}`)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-mono text-gray-600">
                            {truncateId(bundle.id)}
                          </span>
                          <button
                            onClick={(e) => handleCopyId(bundle.id, e)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copy bundle ID"
                          >
                            {copiedId === bundle.id ? (
                              <svg className="h-3.5 w-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            ) : (
                              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {bundle.seller_username || truncateAddress(bundle.seller_address)}
                        </div>
                        {bundle.seller_username && (
                          <div className="text-xs text-gray-500 font-mono">
                            {truncateAddress(bundle.seller_address)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {bundle.collection_image_url && (
                            <img
                              src={bundle.collection_image_url}
                              alt=""
                              className="h-6 w-6 rounded-full object-cover"
                            />
                          )}
                          <span className="text-sm text-gray-900 truncate max-w-[150px]">
                            {bundle.collection_name || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {bundle.item_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatXrp(bundle.price_drops)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getBundleStatusColor(bundle.status)}`}>
                          {bundle.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(bundle.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {bundle.status === 'sold'
                          ? formatDate(bundle.sold_at)
                          : bundle.status === 'cancelled'
                            ? formatDate(bundle.cancelled_at)
                            : formatDate(bundle.expires_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3">
                <div className="text-sm text-gray-700">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} bundles
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(1)}
                    disabled={pagination.page <= 1}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    First
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.page <= 1}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setPage(pagination.totalPages)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
